import { theme } from '@prose-ui';
import { styled } from '@prose-ui/legacy';
import { animated, useSpring } from '@react-spring/web';

import { Typography } from 'Components/Typography';

import useResponsiveVariant from 'utils/useResponsiveVariant';

const Root = styled.div`
  width: 100%;
  height: 64px;
  margin: 0;
  overflow: hidden;
  white-space: nowrap;
  display: flex;
  position: relative;
`;

const StyledTypography = styled(Typography)`
  display: block;
  flex-flow: row;
  align-items: center;
  height: 64px;
  width: max-content;
  flex-wrap: nowrap;
  margin: 0;
  line-height: 64px;
  text-transform: ${({ textTransform }) => textTransform};
  color: ${({ fontcolor }) => fontcolor};
  font-size: ${({ fontSize }) => fontSize};
  font-weight: ${({ fontWeight }) => fontWeight};
`;

type TickerProps = {
  content: string[];
  showDash?: boolean;
  hasBorder?: boolean;
  borderColor?: string;
  color?: string;
  variant?:
    | 'inherit'
    | 'hero1'
    | 'hero2'
    | 'hero-mono'
    | 'h1r-mono'
    | 'h2r-mono'
    | 'h1r'
    | 'h2r'
    | 'h1'
    | 'h2'
    | 'h3'
    | 'h4'
    | 'mono1'
    | 'mono2'
    | 'mono3'
    | 'mono4'
    | 'p1'
    | 'p2'
    | 'p3'
    | 'p4';
  textTransform?: string;
  fontSize?: string;
  fontWeight?: string;
  dataTestId?: string;
  backgroundColor?: string;
};

export const Ticker = ({
  content,
  backgroundColor = 'transparent',
  showDash = true, // To be backward compatible
  hasBorder = false,
  borderColor = 'black',
  color = theme.colors.primary['300'],
  variant = 'mono1',
  textTransform = 'uppercase',
  fontSize = 'inherit',
  fontWeight = 'inherit',
  dataTestId = '',
}: TickerProps) => {
  const { isMobile } = useResponsiveVariant();
  const borderStyle = hasBorder ? `1px solid ${borderColor}` : 'none';

  const animationProps = useSpring({
    from: { transform: 'translateX(0%)' },
    to: { transform: 'translateX(-200%)' },
    loop: true,
    config: { duration: isMobile ? 10000 : 30000 },
  });

  return (
    <Root
      className="remove-in-percy"
      data-testid={dataTestId}
      style={{ borderTop: borderStyle, borderBottom: borderStyle, backgroundColor }}
      tabIndex={0}
    >
      <animated.div
        style={{
          ...animationProps,
          display: 'flex',
          minWidth: '100%',
        }}
      >
        {[...content, ...content, ...content].map((word, index) => (
          <StyledTypography
            key={index}
            fontcolor={color}
            fontSize={fontSize}
            fontWeight={fontWeight}
            transform={textTransform}
            variant={variant}
          >
            {showDash ? (
              <span key={word}>&nbsp;&nbsp;&nbsp;{word}&nbsp;&nbsp;&nbsp;-</span>
            ) : (
              <span key={word}>&nbsp;&nbsp;&nbsp;{word}&nbsp;&nbsp;&nbsp;</span>
            )}
          </StyledTypography>
        ))}
      </animated.div>
    </Root>
  );
};
